import { Select as AntdSelect, Form, Input, InputNumber } from "antd";
import Search from "antd/es/input/Search";
import { FormInstance } from "antd/lib";
import { t } from "i18next";
import { MandatoryField } from "../../../../../components/MandatoryField/MandatoryField";
import { AkciznaMarkicaKategorijaProzivoda } from "../../../../../modules/obligee/exciseStamps/exciseStamps.types";
import {
  clearDoubleSpaceDot,
  clearSpecialCharacters,
  handlePaste,
} from "../../../../../utils/functions";
import { useMarkicaZahtevIsporuceniOpsezi } from "../../../../../utils/hooks/useMarkicaZahtevIsporuceniOpsezi";
interface ZAMBrojZahtevaGroupProps {
  form: FormInstance;
  kategorijaFieldName: string | (string | number)[];
  zahtevFieldName: string | (string | number)[];
  opsegFieldName: string | (string | number)[];
  kolicinaFieldName: string | (string | number)[];
  ambalazaFieldName?: string | (string | number)[];
  listFieldName: string;
  index: number;
  markicaKategorijeId: AkciznaMarkicaKategorijaProzivoda | null;
  showAmbalazaInput?: boolean;
  showKolicinaInput: boolean;
  showOpsegOdDo?: boolean;
  opsegOdFieldName?: string | (string | number)[];
  opsegDoFieldName?: string | (string | number)[];
  showPojedinacniUnos?: boolean;
  pojedinacniUnosFieldName?: string | (string | number)[];
}
const ZAMBrojZahtevaGroup = ({
  form,
  kategorijaFieldName,
  zahtevFieldName,
  opsegFieldName,
  kolicinaFieldName,
  ambalazaFieldName,
  listFieldName,
  index,
  markicaKategorijeId,
  showAmbalazaInput,
  showKolicinaInput,
  opsegOdFieldName,
  opsegDoFieldName,
  pojedinacniUnosFieldName,
  showOpsegOdDo,
  showPojedinacniUnos,
}: ZAMBrojZahtevaGroupProps) => {
  const {
    showAmbalaza,
    setShowAmbalaza,
    brojZahtevaOpsegError,
    handleBrojZahtevaOpsegChange,
    handleVrstaKategorijeOpsegChange,
    isLoadingOpsegKategorije,
    isOpsegKategorijeDisabled,
    opsegKategorije,
    opsezi,
    resetOpsegKategorije,
  } = useMarkicaZahtevIsporuceniOpsezi({
    form,
    zahtevFieldName,
    kategorijaFieldName,
    ambalazaFieldName,
    listFieldName,
    markicaKategorijeId,
    onKategorijaChange: clearOpsegFields,
  });

  const opsegWatchPath =
    Array.isArray(opsegFieldName) && listFieldName
      ? [listFieldName, ...opsegFieldName]
      : opsegFieldName;

  const opsegIdValue = Form.useWatch(opsegWatchPath, form);

  const getWatchPath = (fieldName?: string | (string | number)[]) => {
    if (!fieldName) return undefined;
    return Array.isArray(fieldName) && listFieldName
      ? [listFieldName, ...fieldName]
      : fieldName;
  };

  const opsegOdValue = Form.useWatch(getWatchPath(opsegOdFieldName), form);
  const opsegDoValue = Form.useWatch(getWatchPath(opsegDoFieldName), form);
  const pojedinacniUnosValue = Form.useWatch(
    getWatchPath(pojedinacniUnosFieldName),
    form
  );

  const hasOpsegOd =
    opsegOdValue !== undefined && opsegOdValue !== null && opsegOdValue !== "";
  const hasOpsegDo =
    opsegDoValue !== undefined && opsegDoValue !== null && opsegDoValue !== "";
  const hasPojedinacniUnos =
    pojedinacniUnosValue &&
    Array.isArray(pojedinacniUnosValue) &&
    pojedinacniUnosValue.length > 0;

  const hasOpsegOdDo = hasOpsegOd || hasOpsegDo;
  const hasBothOpsegAndPojedinacni = hasOpsegOdDo && hasPojedinacniUnos;

  // Conditions for mandatory fields:
  // 1. Initially all empty → all MandatoryField
  // 2. User fills opseg od/do → opseg od/do keep MandatoryField, pojedinacni gets regular
  // 3. User fills pojedinacni and opseg od/do are empty → opseg od/do get regular, pojedinacni keeps MandatoryField
  // 4. User fills opseg od/do AND pojedinacni → all three MandatoryField
  const opsegOdLabel =
    hasBothOpsegAndPojedinacni ||
    (!hasPojedinacniUnos && hasOpsegOdDo) ||
    (!hasOpsegOdDo && !hasPojedinacniUnos)
      ? MandatoryField("EVIDENCIJA_MARKICA.OPSEG_OD")
      : t("EVIDENCIJA_MARKICA.OPSEG_OD");

  const opsegDoLabel =
    hasBothOpsegAndPojedinacni ||
    (!hasPojedinacniUnos && hasOpsegOdDo) ||
    (!hasOpsegOdDo && !hasPojedinacniUnos)
      ? MandatoryField("EVIDENCIJA_MARKICA.OPSEG_DO")
      : t("EVIDENCIJA_MARKICA.OPSEG_DO");

  const pojedinacniUnosLabel =
    hasBothOpsegAndPojedinacni ||
    (!hasOpsegOdDo && hasPojedinacniUnos) ||
    (!hasOpsegOdDo && !hasPojedinacniUnos)
      ? MandatoryField("EVIDENCIJA_MARKICA.POJEDINACNI_UNOS")
      : t("EVIDENCIJA_MARKICA.POJEDINACNI_UNOS");

  function clearOpsegFields(isOpseg: boolean = false) {
    if (opsegFieldName && Array.isArray(opsegFieldName) && !isOpseg) {
      form.setFieldValue([listFieldName, ...opsegFieldName], null);
    }
    if (opsegOdFieldName && Array.isArray(opsegOdFieldName)) {
      form.setFieldValue([listFieldName, ...opsegOdFieldName], null);
    }
    if (opsegDoFieldName && Array.isArray(opsegDoFieldName)) {
      form.setFieldValue([listFieldName, ...opsegDoFieldName], null);
    }
    if (pojedinacniUnosFieldName && Array.isArray(pojedinacniUnosFieldName)) {
      form.setFieldValue([listFieldName, ...pojedinacniUnosFieldName], []);
    }
  }

  const handleOpsegIdChange = (value: number) => {
    if (opsegFieldName && Array.isArray(opsegFieldName)) {
      form.setFieldValue([listFieldName, ...opsegFieldName], value);

      clearOpsegFields(true);
    }
  };

  const handleBrojZahtevaOpsegSubmit = async (brojZahteva: string) => {
    if (
      listFieldName &&
      index !== undefined &&
      Array.isArray(kategorijaFieldName) &&
      kategorijaFieldName?.[1] &&
      Array.isArray(opsegFieldName) &&
      opsegFieldName?.[1]
    ) {
      const oldValues = form.getFieldValue(listFieldName);

      const updatedValues = oldValues.map((value: any, i: number) => {
        if (index === i) {
          setShowAmbalaza(false);

          if (Array.isArray(ambalazaFieldName) && ambalazaFieldName?.[1]) {
            return {
              ...value,
              [kategorijaFieldName?.[1]]: null,
              [opsegFieldName?.[1]]: null,
              [ambalazaFieldName?.[1]]: null,
              ...(opsegOdFieldName && showOpsegOdDo
                ? { [opsegOdFieldName?.[1]]: null }
                : {}),
              ...(opsegDoFieldName && showOpsegOdDo
                ? { [opsegDoFieldName?.[1]]: null }
                : {}),
            };
          }

          return {
            ...value,
            [kategorijaFieldName?.[1]]: null,
            [opsegFieldName?.[1]]: null,
            ...(opsegOdFieldName && showOpsegOdDo
              ? { [opsegOdFieldName?.[1]]: null }
              : {}),
            ...(opsegDoFieldName && showOpsegOdDo
              ? { [opsegDoFieldName?.[1]]: null }
              : {}),
          };
        }
        return value;
      });

      form.setFieldValue(listFieldName, updatedValues);
    } else {
      form.setFieldValue(kategorijaFieldName, null);
      form.setFieldValue(opsegFieldName, null);
      if (opsegOdFieldName && showOpsegOdDo) {
        form.setFieldValue(opsegOdFieldName, null);
      }
      if (opsegDoFieldName && showOpsegOdDo) {
        form.setFieldValue(opsegDoFieldName, null);
      }
      if (Array.isArray(ambalazaFieldName)) {
        form.setFieldValue(ambalazaFieldName, null);
      }
      setShowAmbalaza(false);
    }

    resetOpsegKategorije();

    try {
      await handleBrojZahtevaOpsegChange(brojZahteva);
    } catch (err) {
    } finally {
      if (listFieldName) {
        setTimeout(() => {
          form.validateFields([
            [listFieldName, zahtevFieldName?.[0], zahtevFieldName?.[1]],
          ]);
        }, 100);
      }
    }
  };

  const handleOsteceneChange = (value: string[] | string | undefined) => {
    if (!pojedinacniUnosFieldName) return;

    if (!value || !Array.isArray(value)) return;

    const newValue = value.reduce<string[]>((acc, v) => {
      if (typeof v !== "string") return acc;

      const sanitized = v.replace(/\./g, "").replace(/ /g, "").trim();

      if (sanitized) {
        acc.push(sanitized);
      }
      return acc;
    }, []);

    if (Array.isArray(pojedinacniUnosFieldName)) {
      form.setFieldValue(
        [listFieldName, ...pojedinacniUnosFieldName],
        newValue
      );
    } else {
      form.setFieldValue(pojedinacniUnosFieldName, newValue);
    }
  };

  return (
    <>
      <Form.Item
        className="mb-30"
        name={zahtevFieldName}
        colon={false}
        label={MandatoryField("EVIDENCIJA_MARKICA.ZAM_ID_ZAHTEVA")}
        rules={[
          {
            validator: (_, value) => {
              if (!!value && brojZahtevaOpsegError?.brojZahteva === value) {
                return Promise.reject();
              }
              return Promise.resolve();
            },
            message: brojZahtevaOpsegError?.message ?? "",
          },
        ]}
      >
        <Search
          placeholder={t("EVIDENCIJA_MARKICA.ZAM_ID_ZAHTEVA")}
          name="zahtevOpsegId"
          enterButton={t("COMMON.POTVRDI")}
          onSearch={(brojZahteva) => handleBrojZahtevaOpsegSubmit(brojZahteva)}
          // Needed to prevent form from submitting
          onKeyDown={(e) => (e.key == "Enter" ? e.preventDefault() : "")}
          allowClear
        />
      </Form.Item>
      <Form.Item
        name={kategorijaFieldName}
        label={MandatoryField("TABLES.VRSTA_KATEGORIJE")}
        className="mb-30"
        colon={false}
      >
        <AntdSelect
          placeholder={t("TABLES.VRSTA_KATEGORIJE")}
          options={opsegKategorije}
          loading={isLoadingOpsegKategorije}
          disabled={isOpsegKategorijeDisabled}
          onChange={handleVrstaKategorijeOpsegChange}
        />
      </Form.Item>
      <Form.Item
        name={opsegFieldName}
        label={MandatoryField("TABLES.OPSEG")}
        className="mb-30"
        colon={false}
      >
        <AntdSelect
          placeholder={t("TABLES.OPSEG")}
          options={opsezi}
          loading={isLoadingOpsegKategorije}
          disabled={isOpsegKategorijeDisabled}
          onChange={handleOpsegIdChange}
        />
      </Form.Item>
      {showOpsegOdDo && opsegOdFieldName && opsegDoFieldName ? (
        <>
          <Form.Item
            name={opsegOdFieldName}
            label={opsegOdLabel}
            className="mb-30"
            colon={false}
          >
            <Input
              placeholder={t("EVIDENCIJA_MARKICA.OPSEG_OD")}
              disabled={!opsegIdValue}
              onKeyDown={clearSpecialCharacters}
              onPaste={handlePaste}
              onChange={(e) => {
                clearDoubleSpaceDot(e);

                if (!opsegOdFieldName) return;

                if (Array.isArray(opsegOdFieldName)) {
                  form.setFieldValue(
                    [listFieldName, ...opsegOdFieldName],
                    e.target.value
                  );
                } else {
                  form.setFieldValue(opsegOdFieldName, e.target.value);
                }
              }}
            />
          </Form.Item>
          <Form.Item
            name={opsegDoFieldName}
            label={opsegDoLabel}
            className="mb-30"
            colon={false}
          >
            <Input
              placeholder={t("EVIDENCIJA_MARKICA.OPSEG_DO")}
              disabled={!opsegIdValue}
              onKeyDown={clearSpecialCharacters}
              onPaste={handlePaste}
              onChange={(e) => {
                clearDoubleSpaceDot(e);

                if (!opsegDoFieldName) return;

                if (Array.isArray(opsegDoFieldName)) {
                  form.setFieldValue(
                    [listFieldName, ...opsegDoFieldName],
                    e.target.value
                  );
                } else {
                  form.setFieldValue(opsegDoFieldName, e.target.value);
                }
              }}
            />
          </Form.Item>
        </>
      ) : null}
      {showPojedinacniUnos && pojedinacniUnosFieldName ? (
        <Form.Item
          name={pojedinacniUnosFieldName}
          label={pojedinacniUnosLabel}
          className="mb-30"
          colon={false}
          tooltip={t("EVIDENCIJA_MARKICA.POJEDINACNI_UNOS_TOOLTIP", {
            ENTER: "Enter",
          })}
        >
          <AntdSelect
            mode="tags"
            placeholder={t("EVIDENCIJA_MARKICA.POJEDINACNI_UNOS")}
            tokenSeparators={[","]}
            style={{ width: "100%" }}
            open={false}
            suffixIcon={null}
            onInputKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
            onChange={handleOsteceneChange}
            onKeyDown={(e) =>
              clearSpecialCharacters(
                e as React.KeyboardEvent<HTMLInputElement>,
                [","]
              )
            }
          />
        </Form.Item>
      ) : null}
      {showKolicinaInput && (
        <Form.Item
          className="mb-30"
          name={kolicinaFieldName}
          colon={false}
          label={MandatoryField("COMMON.KOLICINA")}
        >
          <InputNumber
            placeholder={t("COMMON.KOLICINA")}
            style={{ width: "100%" }}
            min={0}
          />
        </Form.Item>
      )}
      {showAmbalazaInput && showAmbalaza && (
        <Form.Item
          className="mb-30"
          name={ambalazaFieldName}
          colon={false}
          label={MandatoryField(
            markicaKategorijeId === AkciznaMarkicaKategorijaProzivoda.ALKOHOL
              ? `${t("EVIDENCIJA_MARKICA.ZAPREMINA_AMBALAZE")} (L)`
              : `${t("EVIDENCIJA_MARKICA.KOLICINA")} (kg) ${t(
                  "EVIDENCIJA_MARKICA.PO_POJEDINACNOM_PAKOVAJNU"
                )}`,
            true
          )}
        >
          <InputNumber
            placeholder={t("COMMON.AMBALAZA")}
            style={{ width: "100%" }}
            min={0}
          />
        </Form.Item>
      )}
    </>
  );
};

export default ZAMBrojZahtevaGroup;
